import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface CookiePreferences {
  strictlyNecessary: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    strictlyNecessary: true,
    analytics: false,
    marketing: false,
    timestamp: Date.now(),
  });

  useEffect(() => {
    const savedConsent = localStorage.getItem('cookieConsent');
    if (!savedConsent) {
      setShowBanner(true);
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    setShowBanner(false);
    setShowModal(false);
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      strictlyNecessary: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
    };
    savePreferences(allAccepted);
  };

  const handleRejectNonEssential = () => {
    const essentialOnly: CookiePreferences = {
      strictlyNecessary: true,
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
    };
    savePreferences(essentialOnly);
  };

  const handleSavePreferences = () => {
    savePreferences({ ...preferences, timestamp: Date.now() });
  };

  const handleManagePreferences = () => {
    setShowModal(true);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#2D5F3F] shadow-lg z-50 animate-slide-up">
        <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <i className="ri-cookie-line text-3xl text-[#D4AF37]"></i>
                <h3 className="text-xl font-bold text-[#2D5F3F]">Cookie Consent</h3>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed mb-2">
                We use cookies to enhance your browsing experience, analyze site traffic, and deliver personalized content. 
                Cookies help us understand how you use our website and improve our services in compliance with PIPEDA and CASL.
              </p>
              <p className="text-gray-600 text-xs">
                <strong>Cookie Types:</strong> Strictly Necessary (essential for site functionality), Analytics (usage patterns), 
                and Marketing (personalized ads). Learn more in our{' '}
                <Link to="/privacy" className="text-[#D4AF37] hover:underline cursor-pointer font-semibold">
                  Privacy Policy
                </Link>.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <button
                onClick={handleRejectNonEssential}
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                Reject Non-Essential
              </button>
              <button
                onClick={handleManagePreferences}
                className="px-6 py-3 bg-white border-2 border-[#2D5F3F] text-[#2D5F3F] rounded-lg font-semibold hover:bg-[#F5F1E8] transition-colors whitespace-nowrap cursor-pointer"
              >
                Manage Preferences
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-6 py-3 bg-[#2D5F3F] text-white rounded-lg font-semibold hover:bg-[#1A3A2A] transition-colors whitespace-nowrap cursor-pointer"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Preferences Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-[#2D5F3F] to-[#1A3A2A] text-white p-6 rounded-t-lg">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Cookie Preferences</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-white hover:text-gray-200 transition-colors cursor-pointer"
                  aria-label="Close modal"
                >
                  <i className="ri-close-line text-3xl"></i>
                </button>
              </div>
              <p className="text-gray-200 text-sm mt-2">
                Manage your cookie preferences below. Changes will take effect immediately.
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* Strictly Necessary */}
              <div className="border-b border-gray-200 pb-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-shield-check-line text-xl text-[#2D5F3F]"></i>
                      <h3 className="text-lg font-bold text-[#2D5F3F]">Strictly Necessary</h3>
                      <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full font-semibold">
                        Always Active
                      </span>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      These cookies are essential for the website to function properly. They enable core functionality 
                      such as security, network management, shopping cart, and accessibility. You cannot opt-out of these cookies.
                    </p>
                  </div>
                  <div className="flex items-center">
                    <div className="w-12 h-6 bg-[#2D5F3F] rounded-full relative opacity-50 cursor-not-allowed">
                      <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Analytics */}
              <div className="border-b border-gray-200 pb-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-bar-chart-line text-xl text-[#2D5F3F]"></i>
                      <h3 className="text-lg font-bold text-[#2D5F3F]">Analytics Cookies</h3>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      These cookies help us understand how visitors interact with our website by collecting and reporting 
                      information anonymously. This helps us improve our website's performance and user experience.
                    </p>
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}
                      className={`w-12 h-6 rounded-full relative transition-colors cursor-pointer ${
                        preferences.analytics ? 'bg-[#2D5F3F]' : 'bg-gray-300'
                      }`}
                      aria-label="Toggle analytics cookies"
                    >
                      <div
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          preferences.analytics ? 'right-1' : 'left-1'
                        }`}
                      ></div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Marketing */}
              <div className="pb-2">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-megaphone-line text-xl text-[#2D5F3F]"></i>
                      <h3 className="text-lg font-bold text-[#2D5F3F]">Marketing/Advertising Cookies</h3>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      These cookies are used to deliver advertisements that are relevant to you and your interests. 
                      They also help measure the effectiveness of advertising campaigns and limit the number of times you see an ad.
                    </p>
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
                      className={`w-12 h-6 rounded-full relative transition-colors cursor-pointer ${
                        preferences.marketing ? 'bg-[#2D5F3F]' : 'bg-gray-300'
                      }`}
                      aria-label="Toggle marketing cookies"
                    >
                      <div
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          preferences.marketing ? 'right-1' : 'left-1'
                        }`}
                      ></div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-[#F5F1E8] p-6 rounded-b-lg border-t border-gray-200">
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="px-6 py-3 bg-[#2D5F3F] text-white rounded-lg font-semibold hover:bg-[#1A3A2A] transition-colors whitespace-nowrap cursor-pointer"
                >
                  Save Preferences
                </button>
              </div>
              <p className="text-xs text-gray-600 mt-3 text-center">
                Your preferences will be saved locally and respected across all pages. 
                View our <Link to="/privacy" className="text-[#D4AF37] hover:underline cursor-pointer font-semibold">Privacy Policy</Link> for more details.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}